import { Formik } from 'formik';
import * as Yup from 'yup';
import { setAgencyPaymentAccount, getAgencyPaymentAccount } from '../../../../api/requests';
import Page from '../../../../components/PageWrapper';
import Button from '../../../../components/form-control/Button';
import Input from '../../../../components/form-control/Input';
import InputSelectMenu from '../../../../components/form-control/InputSelectMenu';
import SelectMenu from '../../../../components/form-control/SelectMenu';
import { AGENCY_PAYMENT_ACCOUNT_TYPES, MOBILE_MONEY_NETWORKS } from '../../../../resources/enums';
import { getValueFromFormattedPaymentChannel, showToast } from '../../../../utils/miscFunctions';
import { useState } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import useUserAccount from '../../../../hooks/userAccountHook';
import Loader from '../../../../components/Loader';
import { BANKS_OF_GHANA } from '../../../../resources/constants';


const SettlementAccount = () => {
    const { user } = useUserAccount();
    const NETWORK_LIST: Array<string> = Object.values(MOBILE_MONEY_NETWORKS).map((value) => value);
    const [paymentDetails, setPaymentDetails] = useState<ISettlementAccountForm>({
        accountType: null,
        accountNumber: null,
        accountName: null,
        accountProvider: null
    }
    );
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const invalidateQueries = useQueryClient();

    let teamID: string = user?.company?.id ?? '';

    const getPaymentDetails = async () => {
        try {
            if (teamID) {
                const response: any = await getAgencyPaymentAccount(teamID);
              
                if (response) {
                    setPaymentDetails({
                        accountType: response.accountType,
                        accountNumber: response.accountNumber,
                        accountName: response.accountName,
                        accountProvider: response.accountProvider
                    });
                }
            }
        } catch (error: any) {
            setPaymentDetails({
                accountType: null,
                accountNumber: null,
                accountName: null,
                accountProvider: null
            });
            console.error(error?.description ?? 'Failed to fetch payment details');
        }
    };

    const disableFormEdit = (): boolean => !user.isOwner();

    const { isLoading: paymentDetailsLoading } = useQuery({
        queryKey: ['getPaymentDetails', teamID, paymentDetails],
        queryFn: () => getPaymentDetails(),
        enabled: user.isOwner()
    });

    return (
        <Page title="Business Preference">
            <div className="min-h-full bg-white dark:bg-darkBg w-full p-5">
                {paymentDetailsLoading ? (
                    <Loader message="Fetching account details. Please wait..." brollyLoaderFixed />
                ) : (
                    <div className="bg-white dark:bg-darkBg">
                        <Formik
                            initialValues={{
                                accountType: paymentDetails?.accountType,
                                accountName: paymentDetails?.accountName,
                                accountNumber: paymentDetails?.accountNumber,
                                accountProvider: paymentDetails?.accountProvider
                            }}
                            validateOnBlur
                            validationSchema={Yup.object().shape({
                                accountType: Yup.string().required('account type is required').nullable(),
                                accountName: Yup.string().required('account name is required'),
                                accountNumber: Yup.string().required('account number is required'),
                                accountProvider: Yup.string().required('account provider is required').nullable(),
                            })}
                            onSubmit={(values) => {
                                setIsSubmitting(true);

                                const paymentAccountPayload = {
                                    accountType: values.accountType ? getValueFromFormattedPaymentChannel(values.accountType) : '',
                                    accountName: values.accountName,
                                    accountNumber: values.accountNumber,
                                    accountProvider: values.accountProvider
                                };

                                const handleSuccess = (response: any) => {
                                    showToast.success(response?.message ?? 'Business details updated successfully');
                                    invalidateQueries.invalidateQueries({ queryKey: ['getProfileDetails', 'getPaymentDetails'] });
                                };

                                const handleError = (error: any) => {
                                    showToast.error(error?.description ?? 'Failed to update organization details');
                                    setIsSubmitting(false);
                                };

                                setAgencyPaymentAccount(paymentAccountPayload)
                                    .then((response: any) => {
                                        if (response) {
                                            handleSuccess(response);
                                        }
                                    })
                                    .catch((error: any) => {
                                        handleError(error);
                                    })
                                    .finally(() => {
                                        setIsSubmitting(false);
                                    });
                            }}>
                            {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, errors, touched }) => {
                                return (
                                    <form onSubmit={handleSubmit}>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-[24.5px] items-center justify-center">
                                            <div className="">
                                                <SelectMenu
                                                    label="Account Type"
                                                    name="accountType"
                                                    listSelectedValue={values.accountType}
                                                    onChange={(val: string | null) => {
                                                        setFieldValue('accountType', val);
                                                        if (
                                                            paymentDetails?.accountType ===
                                                            AGENCY_PAYMENT_ACCOUNT_TYPES.BANK &&
                                                            val === AGENCY_PAYMENT_ACCOUNT_TYPES.BANK
                                                        ) {
                                                            setFieldValue(
                                                                'accountProvider',
                                                                paymentDetails.accountProvider ?? ''
                                                            );
                                                        } else if (
                                                            paymentDetails?.accountType ===
                                                            AGENCY_PAYMENT_ACCOUNT_TYPES.MOBILE_MONEY &&
                                                            val === AGENCY_PAYMENT_ACCOUNT_TYPES.MOBILE_MONEY
                                                        ) {
                                                            setFieldValue(
                                                                'accountProvider',
                                                                paymentDetails.accountProvider ?? ''
                                                            );
                                                        } else {
                                                            setFieldValue('accountProvider', '');
                                                        }
                                                    }}
                                                    onBlur={handleBlur}
                                                    list={['BANK', 'MOBILE MONEY']}
                                                    placeholder="Select account type"
                                                    required
                                                    disabled={disableFormEdit()}
                                                    error={touched.accountType ? errors.accountType : undefined}
                                                    helperText={
                                                        touched.accountType ? errors.accountType : undefined
                                                    }
                                                />
                                            </div>

                                            {values?.accountType === AGENCY_PAYMENT_ACCOUNT_TYPES.BANK && (
                                                <>
                                                    <div className="">
                                                        <InputSelectMenu
                                                            label="Bank Name"
                                                            name="accountProvider"
                                                            listSelectedValue={values.accountProvider}
                                                            onChange={(val: string | null) => {
                                                                setFieldValue('accountProvider', val);
                                                            }}
                                                            onBlur={handleBlur}
                                                            list={BANKS_OF_GHANA}
                                                            placeholder="Select or enter bank name"
                                                            required
                                                            disabled={disableFormEdit()}
                                                            editable={disableFormEdit()}
                                                            error={
                                                                touched.accountProvider
                                                                    ? errors.accountProvider
                                                                    : undefined
                                                            }
                                                            helperText={
                                                                touched.accountProvider
                                                                    ? errors.accountProvider
                                                                    : undefined
                                                            }
                                                        />
                                                    </div>
                                                    <div className="">
                                                        <Input
                                                            type="number"
                                                            name="accountNumber"
                                                            label="Account Number"
                                                            value={values.accountNumber || ''}
                                                            onChange={handleChange}
                                                            disabled={disableFormEdit()}
                                                            required
                                                            error={
                                                                touched.accountNumber ? errors.accountNumber : undefined
                                                            }
                                                            helperText={
                                                                touched?.accountNumber ? errors.accountNumber : undefined
                                                            }
                                                        />
                                                    </div>
                                                    <div className="">
                                                        <Input
                                                            type="text"
                                                            name="accountName"
                                                            label="Account Name"
                                                            value={values.accountName || ''}
                                                            onBlur={handleBlur}
                                                            onChange={handleChange}
                                                            required
                                                            disabled={disableFormEdit()}
                                                            error={touched.accountName ? errors.accountName : undefined}
                                                            helperText={
                                                                touched?.accountName ? errors.accountName : undefined
                                                            }
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {values?.accountType === 'MOBILE MONEY' && (
                                                <>
                                                    <div className="">
                                                        <SelectMenu
                                                            label="Network Name"
                                                            name="accountProvider"
                                                            listSelectedValue={values.accountProvider}
                                                            onChange={(val: string | null) => {
                                                                setFieldValue('accountProvider', val);
                                                            }}
                                                            onBlur={handleBlur}
                                                            list={NETWORK_LIST}
                                                            placeholder="Select network name"
                                                            required
                                                            disabled={disableFormEdit()}
                                                            error={
                                                                touched.accountProvider
                                                                    ? errors.accountProvider
                                                                    : undefined
                                                            }
                                                            helperText={
                                                                touched?.accountProvider
                                                                    ? errors.accountProvider
                                                                    : undefined
                                                            }
                                                        />
                                                    </div>

                                                    <div className="">
                                                        <Input
                                                            type="number"
                                                            name="accountNumber"
                                                            label="Account Number"
                                                            value={values.accountNumber || ''}
                                                            onChange={handleChange}
                                                            disabled={disableFormEdit()}
                                                            required
                                                            error={touched.accountNumber ? errors.accountNumber : undefined}
                                                            helperText={touched?.accountNumber ? errors.accountNumber : undefined}
                                                        />
                                                    </div>

                                                    <div className="">
                                                        <Input
                                                            type="text"
                                                            name="accountName"
                                                            label="Account Name"
                                                            value={values.accountName || ''}
                                                            onBlur={handleBlur}
                                                            onChange={handleChange}
                                                            required
                                                            disabled={disableFormEdit()}
                                                            error={touched.accountName ? errors.accountName : undefined}
                                                            helperText={touched?.accountName ? errors.accountName : undefined}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        <div className="flex justify-end mt-10">
                                            <Button
                                                label="Save"
                                                type="submit"
                                                className="px-[2rem] py-[12px] h-[45px] "
                                                bgColor="primary"
                                                size="lg"
                                                curved
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </form>
                                );
                            }}
                        </Formik>
                    </div>
                )}
            </div>
        </Page >
    );
};

export default SettlementAccount;


type ISettlementAccountForm = {
    accountType: string | null;
    accountNumber: string | null;
    accountName: string | null;
    accountProvider: string | null;
}
